import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CategoryStyle, MotifId, RingState, MAX_VISIBLE_RINGS, ALL_MOTIF_IDS } from '../challenge.model';
import { MOTIFS } from './motif-library';

interface MotifChoice { id: MotifId; label: string; }

@Component({
    selector: 'app-mandala-settings',
    templateUrl: './mandala-settings.component.html',
    styleUrls: ['./mandala-settings.component.scss'],
    standalone: false
})
export class MandalaSettingsComponent implements OnInit {
  @Input() rings: RingState[] = [];
  @Input() allActiveCategories: string[] = [];

  @Output() ringOrderChange = new EventEmitter<string[]>();
  @Output() styleChange = new EventEmitter<{ category: string; style: CategoryStyle }>();
  @Output() visibleCategoriesChange = new EventEmitter<string[]>();
  @Output() resetToDefaults = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  readonly motifChoices: MotifChoice[] = ALL_MOTIF_IDS.map(id => ({ id, label: MOTIFS[id].label }));
  readonly maxVisible = MAX_VISIBLE_RINGS;

  ordered: string[] = [];
  visibleCategories: string[] = [];

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.ordered = this.rings.map(r => r.category);
    this.visibleCategories = this.ordered.slice();
  }

  get hasOverflow(): boolean { return this.allActiveCategories.length > this.maxVisible; }

  styleFor(category: string): CategoryStyle {
    const ring = this.rings.find(r => r.category === category);
    return ring ? { motif: ring.motif, color: ring.color } : { motif: 'dot', color: '#d4baee' };
  }

  drop(e: CdkDragDrop<string[]>) {
    moveItemInArray(this.ordered, e.previousIndex, e.currentIndex);
    this.ringOrderChange.emit([...this.ordered]);
  }

  onMotifPick(category: string, motif: MotifId) {
    const cur = this.styleFor(category);
    this.styleChange.emit({ category, style: { motif, color: cur.color } });
  }

  onColorPick(category: string, color: string) {
    const cur = this.styleFor(category);
    this.styleChange.emit({ category, style: { motif: cur.motif, color } });
  }

  isVisible(category: string): boolean {
    return this.visibleCategories.includes(category);
  }

  toggleVisible(category: string) {
    const idx = this.visibleCategories.indexOf(category);
    if (idx >= 0) {
      this.visibleCategories.splice(idx, 1);
    } else if (this.visibleCategories.length < this.maxVisible) {
      this.visibleCategories.push(category);
    } else {
      // At cap; refuse silently. The template disables checkboxes when at cap.
      return;
    }
    this.visibleCategoriesChange.emit([...this.visibleCategories]);
  }

  canCheckMore(category: string): boolean {
    return this.isVisible(category) || this.visibleCategories.length < this.maxVisible;
  }

  motifSvgPreview(motif: MotifId, color: string): SafeHtml {
    // Always show the filled variant in the picker so the preview reads as the
    // motif's full identity, not its empty-shell counterpart.
    return this.sanitizer.bypassSecurityTrustHtml(MOTIFS[motif].render(10, color, true));
  }
}
